// Excess-GFA (竣工超面积) premium.
//
// Per rules/RULES_CHANGE_PREMIUM.md §6:
//   竣工超面积应补缴地价 = 按2.1测算结果 × 130%
//   1. 产权条件按合同约定产权条件。
//   2. 必须经规划确认保留。
//   3. 未确认时应提示人工复核。
//
// The implementation reuses `calculateChangePremium` for the §2.1
// flow and multiplies the resulting premium by 1.3 (EXCESS_GFA_RATIO).
// The "contract property condition" rule maps cleanly to the
// before-snapshot property condition in the change-premium input;
// users that want strict-by-contract property conditions should
// put the contract condition on the before side and any new
// condition on the after side (the delta captures the change).
// If the user has not had the excess area confirmed by planning,
// we emit a warning per rule 3.

import { MessageSink } from "./message-sink.js";

import { calculateChangePremium } from "./change-premium.js";

import type { ChangePremiumInput, ChangePremiumResult } from "../types/change-premium.js";
import type { ValidationMessage } from "../types/validation.js";

/** Premium multiplier per RULES_CHANGE_PREMIUM.md §6. */
export const EXCESS_GFA_RATIO = 1.3;

const PLANNING_UNCONFIRMED_WARNING =
  "竣工超面积需经规划确认保留。未确认时应提示人工复核（RULES_CHANGE_PREMIUM.md §6 注意 3）。";

const RATIO_NOTE = "竣工超面积应补缴地价 = 按2.1测算结果 × 130%（RULES_CHANGE_PREMIUM.md §6）。";

export interface ExcessGfaInput extends ChangePremiumInput {
  /** True only when planning has formally confirmed the excess GFA. */
  readonly planningConfirmed: boolean;
}

export interface ExcessGfaResult {
  /** The underlying change-premium calculation result. */
  readonly base: ChangePremiumResult;
  /** `base.premium × 1.3`. */
  readonly excessGfaPremium: number;
  /** Convenience accessor for `base.validationMessages` + extras. */
  readonly validationMessages: ValidationMessage[];
  /** Note shown to the user (always present). */
  readonly note: string;
}

/**
 * Compute the excess-GFA premium.
 *
 * Steps:
 *   1. Run the standard change-premium flow (§2.1).
 *   2. Multiply `premium` by 1.3 per §6.
 *   3. If `planningConfirmed` is false, append a warning (rule 3).
 *   4. Always attach a note about the 130% rule.
 */
export function calculateExcessGfaPremium(
  input: ExcessGfaInput,
): ExcessGfaResult {
  const topSink = new MessageSink();
  topSink.info("calculationType", RATIO_NOTE);

  if (!input.planningConfirmed) {
    topSink.warning("planningConfirmed", PLANNING_UNCONFIRMED_WARNING);
  }

  const base = calculateChangePremium(input);

  const excessGfaPremium = base.premium * EXCESS_GFA_RATIO;

  // The underlying change-premium result carries validation messages
  // on beforeResult / afterResult rather than at the top level.
  const validationMessages: ValidationMessage[] = [
    ...topSink.toArray(),
    ...base.beforeResult.validationMessages,
    ...base.afterResult.validationMessages,
  ];

  return {
    base,
    excessGfaPremium,
    validationMessages,
    note: RATIO_NOTE,
  };
}